
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { ProfessionalSession, ClientProfile, FeedbackLog } from '../types';
import { BanknotesIcon } from './icons/BanknotesIcon';
import { UsersIcon } from './icons/UsersIcon';
import { QuestionMarkCircleIcon } from './icons/QuestionMarkCircleIcon';
import { ShieldIcon } from './icons/ShieldIcon';
import { sendTestEmail } from '../services/emailService';
import { getFeedbackLogs } from '../services/dbService';
import { TrendingUpIcon } from './icons/TrendingUpIcon';
import { TrophyIcon } from './icons/TrophyIcon';
import { SpinnerIcon } from './icons/SpinnerIcon';
import { GlobeIcon } from './icons/GlobeIcon';
import { CheckCircleIcon } from './icons/CheckCircleIcon';
import { XCircleIcon } from './icons/XCircleIcon';

interface AdminDashboardProps {
    userDatabase: Record<string, ProfessionalSession>;
    currentUserEmail?: string | null;
}

const KpiCard: React.FC<{ icon: React.ReactNode; label: string; value: string | number; subtext?: string }> = ({ icon, label, value, subtext }) => (
    <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex items-center gap-4">
        <div className="flex-shrink-0">{icon}</div>
        <div>
            <p className="text-sm text-gray-500">{label}</p>
            <p className="text-2xl lg:text-3xl font-bold text-gray-900">{value}</p>
            {subtext && <p className="text-xs text-gray-400 mt-1">{subtext}</p>}
        </div>
    </div>
);

const AdminDashboard: React.FC<AdminDashboardProps> = ({ userDatabase, currentUserEmail }) => {
    const [activeTab, setActiveTab] = useState<'analytics' | 'feedback' | 'system'>('analytics');
    const [feedbackLogs, setFeedbackLogs] = useState<FeedbackLog[]>([]);
    const [loadingFeedback, setLoadingFeedback] = useState(false);
    const [excludeTestData, setExcludeTestData] = useState(false);
    const [testEmailStatus, setTestEmailStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
    const [testAIStatus, setTestAIStatus] = useState<'idle' | 'testing' | 'success' | 'error'>('idle');
    const [securityStatus, setSecurityStatus] = useState<any>(null);

    const fetchSecurityStatus = useCallback(async () => {
        try {
            const res = await fetch('/api/security/status');
            if (res.ok) setSecurityStatus(await res.json());
        } catch (e) {}
    }, []);

    useEffect(() => {
        fetchSecurityStatus();
    }, [fetchSecurityStatus]);

    const handleTestAI = async () => {
        setTestAIStatus('testing');
        try {
            const res = await fetch('/api/ai/chat', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ 
                    contents: [{ role: 'user', parts: [{ text: 'Respond with "SECURE_BRIDGE_VERIFIED"' }] }] 
                })
            });
            const data = await res.json();
            if (res.ok && data.text?.includes('SECURE_BRIDGE_VERIFIED')) {
                setTestAIStatus('success');
            } else {
                setTestAIStatus('error');
            }
        } catch (e) {
            setTestAIStatus('error');
        }
    };

    const handleTestEmail = async () => {
        if (!securityStatus?.emailConfigured) return;
        setTestEmailStatus('sending');
        try {
            const res = await fetch('/api/email/send', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    templateId: 'test_template', // Common placeholder
                    templateParams: {
                        to_email: 'test@example.com',
                        message: 'Diagnostic test from ForgivePath SecureBridge'
                    }
                })
            });
            if (res.ok) setTestEmailStatus('success');
            else setTestEmailStatus('error');
        } catch (e) {
            setTestEmailStatus('error');
        }
    };

    useEffect(() => {
        if (activeTab === 'feedback') {
            setLoadingFeedback(true);
            getFeedbackLogs().then(logs => {
                setFeedbackLogs(logs);
                setLoadingFeedback(false);
            });
        }
    }, [activeTab]);

    const handleRunEmailDiagnostic = async () => {
        setTestEmailStatus('sending');
        try {
            const result = await sendTestEmail('YASEnterpriseLLC@gmail.com');
            if (result.success) setTestEmailStatus('success');
            else setTestEmailStatus('error');
        } catch (e) {
            setTestEmailStatus('error');
        }
    };

    const envCheck = useMemo(() => {
        const meta = import.meta as any;
        const env = meta.env || {};
        
        const mask = (val: string | undefined) => {
            if (!val) return 'MISSING';
            if (val.length < 8) return '***';
            return val.substring(0, 4) + '...' + val.substring(val.length - 4);
        };

        return {
            stripe: { status: !!env.VITE_STRIPE_PAYMENT_LINK, value: mask(env.VITE_STRIPE_PAYMENT_LINK) },
            firebaseKey: { status: !!env.VITE_FIREBASE_API_KEY && env.VITE_FIREBASE_API_KEY.startsWith('AIza'), value: mask(env.VITE_FIREBASE_API_KEY) },
            emailService: { status: !!env.VITE_EMAILJS_SERVICE_ID, value: mask(env.VITE_EMAILJS_SERVICE_ID) },
            emailTemplate: { status: !!env.VITE_EMAILJS_TEMPLATE_ID, value: mask(env.VITE_EMAILJS_TEMPLATE_ID) },
            emailWelcome: { status: !!env.VITE_EMAILJS_WELCOME_TEMPLATE_ID && env.VITE_EMAILJS_WELCOME_TEMPLATE_ID !== 'welcome_template_id', value: mask(env.VITE_EMAILJS_WELCOME_TEMPLATE_ID) },
            emailPublic: { status: !!env.VITE_EMAILJS_PUBLIC_KEY, value: mask(env.VITE_EMAILJS_PUBLIC_KEY) }
        };
    }, []);

    const analytics = useMemo(() => {
        const allClients: ClientProfile[] = [];
        const allSubmissions: any[] = [];
        let totalRevenue = 0;
        let totalForgiven = 0;
        let paidUsersCount = 0;

        Object.values(userDatabase).forEach((session: ProfessionalSession) => {
            Object.values(session.clients).forEach((client: ClientProfile) => {
                if (excludeTestData && client.isTester) return;
                allClients.push(client);
                if (client.paymentStatus === 'paid') {
                    totalRevenue += 79;
                    paidUsersCount++;
                }
                client.submissionLogs.forEach(log => {
                    if (excludeTestData && log.mode === 'test') return;
                    allSubmissions.push({ ...log, clientName: client.userData.name, clientEmail: client.userData.email });
                });
            });
        });

        allSubmissions.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
        totalForgiven = feedbackLogs.reduce((acc, log) => acc + (log.forgivenAmount || 0), 0);

        return {
            totalClients: allClients.length,
            paidUsersCount,
            totalRevenue,
            totalForgiven,
            totalSubmissions: allSubmissions.length,
            recentSubmissions: allSubmissions,
        };
    }, [userDatabase, excludeTestData, feedbackLogs]);

    return (
        <div className="space-y-8">
            <div className="flex flex-col md:flex-row justify-between md:items-center gap-4">
                <div>
                    <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight text-gray-900 uppercase tracking-tighter">Founder Hub</h1>
                    <div className="flex gap-4 mt-6">
                        <button onClick={()=>setActiveTab('analytics')} className={`px-5 py-2.5 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${activeTab==='analytics'?'bg-blue-600 text-white shadow-lg':'bg-white text-gray-400 border border-gray-100 hover:bg-gray-50'}`}>Analytics</button>
                        <button onClick={()=>setActiveTab('feedback')} className={`px-5 py-2.5 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${activeTab==='feedback'?'bg-blue-600 text-white shadow-lg':'bg-white text-gray-400 border border-gray-100 hover:bg-gray-50'}`}>Support</button>
                        <button onClick={()=>setActiveTab('system')} className={`px-5 py-2.5 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${activeTab==='system'?'bg-blue-600 text-white shadow-lg':'bg-white text-gray-400 border border-gray-100 hover:bg-gray-50'}`}>Diagnostics</button>
                    </div>
                </div>
            </div>

            {activeTab === 'analytics' && (
                <>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        <KpiCard icon={<div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center"><BanknotesIcon className="w-7 h-7 text-green-600" /></div>} label="Revenue" value={`$${analytics.totalRevenue.toLocaleString()}`} subtext={`${analytics.paidUsersCount} units sold`} />
                        <KpiCard icon={<div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center"><UsersIcon className="w-7 h-7 text-blue-600" /></div>} label="Accounts" value={analytics.totalClients} subtext="Total registered users" />
                        <KpiCard icon={<div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center"><TrophyIcon className="w-7 h-7 text-purple-600" /></div>} label="Forgiven" value={`$${(analytics.totalForgiven / 1000).toFixed(1)}k`} subtext="Impact verified" />
                        <KpiCard icon={<div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center"><TrendingUpIcon className="w-7 h-7 text-indigo-600" /></div>} label="Conv. Rate" value={`${analytics.totalClients > 0 ? ((analytics.paidUsersCount / analytics.totalClients) * 100).toFixed(1) : 0}%`} />
                    </div>
                    <div className="bg-white p-6 rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
                        <h3 className="text-lg font-bold mb-4 uppercase tracking-widest text-gray-400">Master Ledger</h3>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm text-left">
                                <thead className="bg-gray-50 text-gray-400 uppercase text-[10px] tracking-widest"><tr><th className="p-4 font-black">Date</th><th className="p-4 font-black">Client</th><th className="p-4 font-black">Submission</th></tr></thead>
                                <tbody className="divide-y divide-gray-100">
                                    {analytics.recentSubmissions.map((sub, i) => (
                                        <tr key={i} className="hover:bg-gray-50/50 transition-colors">
                                            <td className="p-4 text-gray-500">{new Date(sub.timestamp).toLocaleDateString()}</td>
                                            <td className="p-4"><strong>{sub.clientName}</strong><p className="text-[10px] text-gray-400">{sub.clientEmail}</p></td>
                                            <td className="p-4 text-xs font-bold text-blue-600">{sub.id}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </>
            )}

            {activeTab === 'system' && (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    <div className="lg:col-span-1 space-y-8">
                        <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm">
                            <h3 className="text-xl font-black text-gray-900 mb-6 uppercase tracking-tighter">Production Health</h3>
                            
                            <div className="bg-green-50 border-2 border-green-100 p-6 rounded-3xl mb-8 animate-fade-in">
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="p-2 bg-green-500 text-white rounded-lg">
                                        <ShieldIcon className="w-5 h-5" />
                                    </div>
                                    <div>
                                        <h4 className="text-sm font-black text-green-900 uppercase tracking-tight">Security Protocol: ACTIVE</h4>
                                        <p className="text-[10px] text-green-600 font-bold uppercase">All sensitive keys are now isolated on the secure server.</p>
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 gap-3">
                                    <div className="bg-white p-3 rounded-2xl border border-green-200">
                                        <div className="flex items-center justify-between mb-2">
                                            <p className="text-[9px] text-gray-400 font-black uppercase tracking-widest">AI Proxy Engine</p>
                                            <button 
                                                onClick={handleTestAI}
                                                disabled={testAIStatus === 'testing'}
                                                className={`text-[8px] font-black px-2 py-1 rounded-md uppercase transition-all ${
                                                    testAIStatus === 'success' ? 'bg-green-100 text-green-700' : 
                                                    testAIStatus === 'error' ? 'bg-red-100 text-red-700' : 
                                                    'bg-blue-50 text-blue-600 hover:bg-blue-100'
                                                }`}
                                            >
                                                {testAIStatus === 'testing' ? 'Testing...' : 
                                                 testAIStatus === 'success' ? 'Verified' : 
                                                 testAIStatus === 'error' ? 'Failed' : 'Run Test'}
                                            </button>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <div className={`w-2 h-2 rounded-full ${securityStatus?.geminiConfigured ? 'bg-green-500' : 'bg-gray-300 animate-pulse'}`}></div>
                                            <span className="text-xs font-black">{securityStatus?.geminiConfigured ? 'ROOTED & SECURE' : 'UNCONFIGURED'}</span>
                                        </div>
                                    </div>

                                    <div className="bg-white p-3 rounded-2xl border border-green-200">
                                        <div className="flex items-center justify-between mb-2">
                                            <p className="text-[9px] text-gray-400 font-black uppercase tracking-widest">Email Gateway</p>
                                            <button 
                                                onClick={handleTestEmail}
                                                disabled={testEmailStatus === 'sending' || !securityStatus?.emailConfigured}
                                                className={`text-[8px] font-black px-2 py-1 rounded-md uppercase transition-all ${
                                                    testEmailStatus === 'success' ? 'bg-green-100 text-green-700' : 
                                                    testEmailStatus === 'error' ? 'bg-red-100 text-red-700' : 
                                                    'bg-blue-50 text-blue-600 hover:bg-blue-100'
                                                }`}
                                            >
                                                {testEmailStatus === 'sending' ? 'Sending...' : 
                                                 testEmailStatus === 'success' ? 'Delivered' : 
                                                 testEmailStatus === 'error' ? 'Failed' : 'Ping Gateway'}
                                            </button>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <div className={`w-2 h-2 rounded-full ${securityStatus?.emailConfigured ? 'bg-green-500' : 'bg-gray-300 animate-pulse'}`}></div>
                                            <span className="text-xs font-black">{securityStatus?.emailConfigured ? 'BRIDGE SECURED' : 'UNCONFIGURED'}</span>
                                        </div>
                                    </div>
                                </div>
                                <div className="mt-4 pt-4 border-t border-green-100 flex justify-between items-center">
                                    <p className="text-[9px] text-green-600 font-bold italic">Protected by ForgivePath SecureBridge</p>
                                    <p className="text-[8px] text-green-400 font-mono">{securityStatus?.timestamp}</p>
                                </div>
                            </div>

                            <h4 className="text-xs font-black text-gray-400 uppercase tracking-widest mb-4 ml-1">Client-Side Indicators</h4>
                            <ul className="space-y-4">
                                {[
                                    { label: 'Firebase API Key', status: envCheck.firebaseKey.status, value: envCheck.firebaseKey.value },
                                    { label: 'Stripe Payments', status: envCheck.stripe.status, value: envCheck.stripe.value },
                                    { label: 'EmailJS Service', status: envCheck.emailService.status, value: envCheck.emailService.value },
                                    { label: 'EmailJS Template', status: envCheck.emailTemplate.status, value: envCheck.emailTemplate.value },
                                    { label: 'EmailJS Key', status: envCheck.emailPublic.status, value: envCheck.emailPublic.value },
                                ].map(item => (
                                    <li key={item.label} className="flex flex-col p-4 bg-gray-50 rounded-2xl border border-gray-100">
                                        <div className="flex items-center justify-between mb-1">
                                            <span className="text-xs font-black text-gray-600 uppercase tracking-widest">{item.label}</span>
                                            {item.status ? (
                                                <CheckCircleIcon className="w-5 h-5 text-green-500" />
                                            ) : (
                                                <div className="flex items-center gap-2">
                                                    <span className="text-[8px] font-bold text-red-400 uppercase">Missing</span>
                                                    <XCircleIcon className="w-5 h-5 text-red-500" />
                                                </div>
                                            )}
                                        </div>
                                        <p className="text-[9px] font-mono text-gray-400">{item.value}</p>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div className="bg-blue-900 p-8 rounded-3xl shadow-xl text-white">
                            <h3 className="text-xl font-black mb-4 uppercase tracking-tighter">Firebase Config</h3>
                            <div className="space-y-3 text-[10px] font-mono">
                                <div>
                                    <p className="text-blue-300 uppercase font-black mb-1">Project ID</p>
                                    <p className="bg-blue-800 p-2 rounded border border-blue-700">{(import.meta as any).env.VITE_FIREBASE_PROJECT_ID || 'Not Found'}</p>
                                </div>
                                <div>
                                    <p className="text-blue-300 uppercase font-black mb-1">Auth Domain</p>
                                    <p className="bg-blue-800 p-2 rounded border border-blue-700">{(import.meta as any).env.VITE_FIREBASE_AUTH_DOMAIN || 'Not Found'}</p>
                                    {!(import.meta as any).env.VITE_FIREBASE_AUTH_DOMAIN?.includes('.firebaseapp.com') && (
                                        <p className="text-red-300 mt-1 font-bold">⚠️ Must end in .firebaseapp.com</p>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="lg:col-span-2 space-y-8">
                        <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm">
                            <h3 className="text-xl font-black text-gray-900 mb-2 uppercase tracking-tighter">Email Diagnostics</h3>
                            <p className="text-sm text-gray-500 mb-6">Verify your EmailJS production keys are working. This sends a test email to <strong>YASEnterpriseLLC@gmail.com</strong>.</p>
                            
                            <div className="space-y-4">
                                <button 
                                    onClick={handleRunEmailDiagnostic} 
                                    disabled={testEmailStatus === 'sending'}
                                    className="w-full bg-blue-600 hover:bg-blue-700 text-white font-black py-4 rounded-2xl shadow-lg transition-all flex items-center justify-center gap-3 uppercase tracking-widest text-xs"
                                >
                                    {testEmailStatus === 'sending' ? <SpinnerIcon className="w-5 h-5 animate-spin"/> : <GlobeIcon className="w-5 h-5"/>}
                                    Send Live Test Email
                                </button>
                                
                                {testEmailStatus === 'success' && <div className="p-4 bg-green-50 border border-green-200 rounded-xl text-green-700 font-bold text-center text-sm">✓ Success! Check your Gmail inbox.</div>}
                                {testEmailStatus === 'error' && <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 font-bold text-center text-sm">✗ Failed. Check console (F12) for logs.</div>}
                            </div>
                        </div>

                        <div className="bg-gray-900 p-8 rounded-3xl shadow-xl text-white">
                            <h3 className="text-xl font-black mb-4 uppercase tracking-tighter">Developer Tip</h3>
                            <p className="text-gray-400 text-sm leading-relaxed">
                                To test the <strong>Welcome Email</strong> without creating real accounts, use a Gmail alias like <code>YASEnterpriseLLC+test1@gmail.com</code>. 
                                Each alias is treated as a "new user" by the system, allowing you to re-trigger the welcome logic infinitely.
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {activeTab === 'feedback' && (
                <div className="space-y-6">
                    {loadingFeedback ? (
                        <div className="text-center py-20"><SpinnerIcon className="w-10 h-10 animate-spin mx-auto text-blue-600"/></div>
                    ) : (
                        <div className="grid grid-cols-1 gap-6">
                            {feedbackLogs.length === 0 ? (
                                <div className="text-center py-20 bg-white rounded-3xl border-2 border-dashed border-gray-100 text-gray-300 font-bold uppercase tracking-widest">No support logs found</div>
                            ) : feedbackLogs.map((log: FeedbackLog) => (
                                <div key={log.id} className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm">
                                    <div className="flex justify-between items-start mb-4">
                                        <div className="flex items-center gap-3">
                                            <div className={`p-2 rounded-xl ${log.category==='success'?'bg-green-100 text-green-600':'bg-blue-100 text-blue-600'}`}>
                                                {log.category==='success'?<TrophyIcon className="w-5 h-5"/>:<ShieldIcon className="w-5 h-5"/>}
                                            </div>
                                            <div>
                                                <h4 className="font-black text-gray-900 uppercase tracking-tighter text-lg">{log.category} from {log.userEmail}</h4>
                                                <p className="text-xs text-gray-400 font-bold uppercase">{new Date(log.timestamp).toLocaleString()} • Stage: {log.appState}</p>
                                            </div>
                                        </div>
                                        <span className={`text-[10px] font-black px-3 py-1 rounded-full uppercase tracking-widest ${log.status==='pending'?'bg-yellow-100 text-yellow-700':'bg-green-100 text-green-700'}`}>{log.status}</span>
                                    </div>
                                    <div className="bg-gray-50 p-6 rounded-2xl text-sm font-medium text-gray-700 leading-relaxed italic border border-gray-100">"{log.content}"</div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};
export default AdminDashboard;
